import { useMediaQuery, useTheme } from '@mui/material';
import type { GridColumnVisibilityModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { useOrderByParams } from '~/hooks/order-by';
import AddUsersIllustration from '~/images/illustrations/add-users.svg';
import type { NetworkUsers__NetworkMembership as NetworkMembership } from '../../queries/list.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AddUsersIllustration}
      description={t('pages.users.list.empty_state.description')}
      header={t('pages.users.list.empty_state.header')}
    />
  );
};

export interface TableProps {
  loading: boolean;
  networkMemberships: readonly NetworkMembership[];
}

export const Table = ({ loading, networkMemberships }: TableProps) => {
  const columns = useColumns();
  const [userColumns, setUserColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumAndDown = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setUserColumns((x) => ({
      ...x,
      email: !isMediumAndDown,
      roles: !isLargeAndDown,
      signInCount: !isLargeAndDown,
      status: !isLargeAndDown,
    }));
  }, [isMediumAndDown, isLargeAndDown, isSmallAndDown]);

  return (
    <ScrollableDataGrid
      checkboxSelection={false}
      columns={columns}
      columnVisibilityModel={userColumns}
      disableRowSelectionOnClick
      loading={loading}
      onColumnVisibilityModelChange={setUserColumns}
      onRowClick={({ row: nm }: GridRowParams<NetworkMembership>) => navigate(String(nm.user.id))}
      rows={networkMemberships}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
